import React from 'react';

/**
 * PageTitle — Standard title block for every marketing / info page of the site.
 *
 * Mirrors the hero style of the /respiration page (HomePageV2 "La rééducation
 * des troubles respiratoires"): a small uppercase cyan kicker with wide
 * letter-spacing, a large Outfit fontWeight-300 headline in slate-900, and an
 * optional solid-cyan accent word.
 *
 * Props:
 *  - kicker:     optional short uppercase line above the title
 *  - title:      main title text (plain part when using `highlight`)
 *  - highlight:  optional word/phrase rendered in solid cyan at the end of the title
 *  - subtitle:   optional supporting paragraph below the title
 *  - align:      'center' (default) or 'left'
 *  - dark:       true when placed on a dark background (switches ink to white)
 *  - className:  extra classes on the wrapper
 */
export function PageTitle({
  kicker,
  title,
  highlight,
  subtitle,
  align = 'center',
  dark = false,
  className = '',
}) {
  const alignCls = align === 'left' ? 'text-left' : 'text-center';
  const inkColor = dark ? '#FFFFFF' : '#0F172A'; // slate-900
  const subInk = dark ? 'rgba(255,255,255,0.75)' : '#334155'; // slate-700
  const cyan = '#0EA5E9'; // sky-500 (same as HomePageV2 accent)
  const kickerCyan = '#0284C7'; // sky-600 (same as HomePageV2 kicker)

  return (
    <div className={`${alignCls} ${className}`}>
      {kicker ? (
        <p
          className="rs-page-title-kicker text-xs md:text-sm uppercase tracking-[0.3em] mb-6 font-semibold"
          style={{ color: kickerCyan }}
          data-testid="page-title-kicker"
        >
          {kicker}
        </p>
      ) : null}

      <h1
        className={`rs-page-title text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 tracking-tight${align === 'center' ? ' lg:whitespace-nowrap' : ''}`}
        style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: inkColor }}
        data-testid="page-title-heading"
      >
        {title}
        {highlight ? (() => {
          // Elision-friendly: if the title ends with an apostrophe (or hyphen)
          // do not insert a space before the highlight (e.g. "de l’" + "Asthme").
          const noSpace = /[’'‑\-]\s*$/.test(title);
          return (
            <>
              {noSpace ? '' : ' '}
              <span
                className="font-normal bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(90deg, #38BDF8 0%, #0EA5E9 45%, #2563EB 100%)',
                }}
              >
                {highlight}
              </span>
            </>
          );
        })() : null}
      </h1>

      {subtitle ? (
        <p
          className={`rs-page-title-subtitle text-base lg:text-lg leading-relaxed ${align === 'center' ? 'max-w-2xl mx-auto' : 'max-w-2xl'}`}
          style={{ color: subInk }}
          data-testid="page-title-subtitle"
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

export default PageTitle;
